import { useState } from 'react';
import type { ApiKey, ApiPreset, IssuedApiKey, NewApiKey } from '@/api/types';
import { PRESETS, scopesLabel } from '@/lib/apiKeys';
import { formatAgo, formatDate } from '@/lib/format';
import { ConfirmButton } from './ConfirmDialog';
import { Dialog, Status, type Tone } from './ui';

/** «Интеграции» (MK42-6384): keys of the public API, shared by the client's settings and the studio's client card. */

function keyState(k: ApiKey): { tone: Tone; label: string } {
  if (k.revokedAt) return { tone: 'neutral', label: 'Отозван' };
  if (!k.live) return { tone: 'neutral', label: 'Истёк' };
  if (k.replacedById) return { tone: 'waiting', label: `Заменён, работает до ${formatDate(k.expiresAt)}` };
  return { tone: 'done', label: 'Действует' };
}

/** The key, once: after closing only its start is ever shown again. */
export function SecretDialog({ issued, rotatedFrom, onClose }: { issued: IssuedApiKey | null; rotatedFrom?: ApiKey; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const close = () => (setCopied(false), onClose());
  return (
    <Dialog open={!!issued} onClose={close} title={rotatedFrom ? `Новый ключ «${issued?.name}»` : `Ключ «${issued?.name}»`}>
      <div className="space-y-3 p-5">
        <pre data-testid="api-key-secret" className="whitespace-pre-wrap break-all rounded bg-sunken p-3 font-mono text-sm">
          {issued?.secret}
        </pre>
        <p role="alert" className="text-sm">
          Скопируйте ключ сейчас: он показывается один раз, мы храним только его отпечаток. Потеряете — перевыпустите.
        </p>
        {rotatedFrom && <p className="text-sm text-muted">Старый ключ работает до {formatDate(rotatedFrom.expiresAt)}: замените его в системе, а когда он перестанет вызываться — отзовите.</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={async () => {
              await navigator.clipboard?.writeText(issued?.secret ?? '').catch(() => undefined);
              setCopied(true);
            }}
          >
            {copied ? 'Скопировано' : 'Скопировать'}
          </button>
          <button type="button" className="btn-primary" onClick={close}>
            Готово
          </button>
        </div>
      </div>
    </Dialog>
  );
}

type Project = { id: string; title: string };

function IssueForm({ projects, onIssue, pending, onCancel }: { projects: Project[]; onIssue: (v: NewApiKey) => void; pending: boolean; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [preset, setPreset] = useState<ApiPreset>('accounting');
  const [projectId, setProjectId] = useState('');
  return (
    <form
      className="space-y-3 rounded bg-panel p-4 shadow-hair"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onIssue({ name: name.trim(), preset, ...(projectId ? { projectId } : {}) });
      }}
    >
      <label className="block text-sm">
        <span className="mb-1.5 block text-muted">Название: для какой системы ключ</span>
        <input className="field" placeholder="1С бухгалтерия" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <fieldset className="space-y-2 text-sm">
        <legend className="mb-1.5 text-muted">Права</legend>
        {PRESETS.map((p) => (
          <label key={p.value} className="flex items-start gap-2">
            <input type="radio" name="preset" className="mt-1" checked={preset === p.value} onChange={() => setPreset(p.value)} />
            <span>
              <span className="font-medium">{p.label}</span>
              <span className="block text-xs text-muted">{p.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {projects.length > 1 && (
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Проект</span>
          <select className="field" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Все проекты</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </label>
      )}
      <p className="text-xs text-muted">Ключ действует год. Права и проект потом не меняются: нужен другой набор — выпустите новый ключ.</p>
      <div className="flex gap-2">
        <button className="btn-primary" disabled={pending || !name.trim()}>
          Выпустить ключ
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Отмена
        </button>
      </div>
    </form>
  );
}

export type KeyActions = {
  issue: (v: NewApiKey) => Promise<IssuedApiKey>;
  rotate: (id: string) => Promise<IssuedApiKey>;
  revoke: (id: string) => Promise<unknown>;
  pending: boolean;
};

/** The list of keys; with `actions` also issuing, rotating and revoking. */
export function ApiKeys({ keys, projects = [], actions, empty }: { keys: ApiKey[]; projects?: Project[]; actions?: KeyActions; empty: string }) {
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ key: IssuedApiKey; from?: ApiKey } | null>(null);
  const [problem, setProblem] = useState('');
  const run = (p: Promise<unknown>) => (setProblem(''), p.catch((e: Error) => setProblem(e.message)));
  const title = (id: string | null) => projects.find((p) => p.id === id)?.title;
  // Ended keys stay in the list for the history, after the working ones.
  const shown = [...keys].sort((a, b) => Number(b.live) - Number(a.live));
  return (
    <>
      {shown.length === 0 ? (
        <p className="text-sm text-muted">{empty}</p>
      ) : (
        <ul className="divide-y divide-line border-y border-line">
          {shown.map((k) => {
            const st = keyState(k);
            const project = k.projectId && title(k.projectId);
            return (
              <li key={k.id} className="py-3" data-testid={`api-key-${k.id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className={k.live ? 'text-sm font-medium' : 'text-sm font-medium text-muted'}>{k.name}</span>
                  <code className="text-xs text-muted">{k.prefix}…</code>
                  <Status tone={st.tone} className="ml-auto">
                    {st.label}
                  </Status>
                </div>
                <p className="mt-1 text-xs text-muted">
                  {scopesLabel(k.scopes)}
                  {project && ` · проект «${project}»`}
                </p>
                <p className="text-xs text-faint">
                  Выпущен {formatDate(k.createdAt)}
                  {k.createdBy && `, ${k.createdBy}`} · до {formatDate(k.expiresAt)} · {k.lastUsedAt ? `последний вызов ${formatAgo(k.lastUsedAt)}` : 'вызовов не было'}
                </p>
                {actions && k.live && (
                  <div className="mt-2 flex gap-1">
                    {!k.replacedById && (
                      <ConfirmButton
                        label="Перевыпустить"
                        className="btn-ghost h-7 px-2 text-xs"
                        title={`Перевыпустить «${k.name}»?`}
                        confirmLabel="Перевыпустить"
                        disabled={actions.pending}
                        onConfirm={() => run(actions.rotate(k.id).then((n) => setIssued({ key: n, from: { ...k, expiresAt: new Date(Math.min(Date.parse(k.expiresAt), Date.now() + 7 * 86_400_000)).toISOString() } })))}
                      >
                        Выпустим новый ключ с теми же правами. Старый проработает ещё 7 дней, чтобы вы успели заменить его в системе.
                      </ConfirmButton>
                    )}
                    <ConfirmButton
                      label="Отозвать"
                      className="btn-ghost h-7 px-2 text-xs"
                      title={`Отозвать «${k.name}»?`}
                      confirmLabel="Отозвать"
                      quiet
                      disabled={actions.pending}
                      onConfirm={() => run(actions.revoke(k.id))}
                    >
                      Ключ перестанет работать сразу: система, которая им пользуется, получит отказ. Вернуть его нельзя, только выпустить новый.
                    </ConfirmButton>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {problem && (
        <p role="alert" className="mt-2 text-sm text-overdue">
          {problem}
        </p>
      )}
      {actions &&
        (adding ? (
          <div className="mt-4">
            <IssueForm projects={projects} pending={actions.pending} onCancel={() => setAdding(false)} onIssue={(v) => run(actions.issue(v).then((n) => (setAdding(false), setIssued({ key: n }))))} />
          </div>
        ) : (
          <button className="btn-secondary mt-3" onClick={() => setAdding(true)}>
            Выпустить ключ
          </button>
        ))}
      <SecretDialog issued={issued?.key ?? null} rotatedFrom={issued?.from} onClose={() => setIssued(null)} />
    </>
  );
}
